import { styled } from "goober";
import { Fragment } from "react";
import {
  type ConnectorWithOAuth,
  isItemVisible,
} from "../../../shared/connectors/getTargetValue";
import { isResourceListField } from "../../../shared/connectors/listRows";
import type { ConnectorDefinition } from "../../../shared/connectors/types";
import { Text } from "../Text";
import { CopyableFieldItem } from "./CopyableFieldItem";
import { FieldItem } from "./FieldItem";
import { InstructionsStep, StepHeading } from "./InstructionsStep";
import { FileFieldItem } from "./FileFieldItem";
import { ListFieldItem } from "./ListFieldItem";
import { ResourceListField } from "./ResourceListField";
import { MarkdownItem } from "./MarkdownItem";
import { NoteField } from "./NoteField";
import { GithubAppManifestFieldItem } from "./GithubAppManifestFieldItem";
import { OAuthFieldItem } from "./OAuthFieldItem";
import { TestUrlField } from "./TestUrlField";
import type { ConnectorAuthorize } from "./useConnectorAuthorize";
import { UrlPatternField } from "./UrlPatternField";
import { ConnectorRoutesItem } from "./ConnectorRoutesItem";
import { WorkflowPickerItem } from "./WorkflowPickerItem";

const Container = styled("form")`
  display: flex;
  flex-direction: column;
`;

export type App = ConnectorWithOAuth;

type Props = {
  definition: ConnectorDefinition;
  app: App;
  showInstructions?: boolean;
  disabled?: boolean;
  onEdit?: () => void;
  locallySet: ReadonlySet<string>;
  onFieldSet: (key: string) => void;
  auth: ConnectorAuthorize;
};

export function Form({
  definition,
  app,
  showInstructions = true,
  disabled,
  onEdit,
  locallySet,
  onFieldSet,
  auth,
}: Props) {
  const Step = showInstructions ? InstructionsStep : Fragment;
  const Wrapper = showInstructions ? Fragment : InstructionsStep;

  return (
    <Wrapper>
      <Container autoComplete="off" style={{ gap: showInstructions ? 24 : 16 }}>
        {definition.steps.map((step, stepIndex) => {
          const title =
            definition.steps.length > 1
              ? `Step ${stepIndex + 1}: ${step.title}`
              : step.title;
          return (
            <Step key={title}>
              {showInstructions && (
                <StepHeading>
                  <Text variant="text-regular-md">{title}</Text>
                </StepHeading>
              )}
              {step.items.map((item) => {
                if (!isItemVisible(item, app, definition)) return null;

                const isNameField =
                  "target" in item &&
                  item.target.kind === "connectedApp" &&
                  item.target.field === "name";

                if (item.kind === "markdown") {
                  if (!showInstructions) return null;
                  const key =
                    typeof item.content === "string"
                      ? item.content
                      : item.content.toString();
                  return (
                    <MarkdownItem
                      key={`${title}:${key}`}
                      item={item}
                      configuration={app.configuration}
                    />
                  );
                }

                if (item.kind === "copyable")
                  return (
                    <CopyableFieldItem key={item.label} item={item} app={app} />
                  );

                if (item.kind === "oauth")
                  return (
                    <OAuthFieldItem
                      app={app}
                      definition={definition}
                      disabled={disabled}
                      auth={auth}
                    />
                  );

                if (item.kind === "githubAppManifest")
                  return (
                    <GithubAppManifestFieldItem
                      key="github-app-manifest"
                      app={app}
                      definition={definition}
                      disabled={disabled}
                    />
                  );

                if (item.kind === "file")
                  return (
                    <FileFieldItem
                      key={`${item.target.kind}:${item.target.field}`}
                      item={item}
                      app={app}
                      disabled={disabled}
                    />
                  );

                if (item.kind === "url-pattern")
                  return (
                    <UrlPatternField
                      key={item.target.field}
                      item={item}
                      app={app}
                      disabled={disabled}
                    />
                  );

                if (item.kind === "list") {
                  const ListComponent = isResourceListField(item)
                    ? ResourceListField
                    : ListFieldItem;
                  return (
                    <ListComponent
                      key={`${item.prefix}:${item.label}`}
                      item={item}
                      app={app}
                      disabled={disabled}
                      onEdit={onEdit}
                    />
                  );
                }

                if (item.kind === "test-url")
                  return (
                    <TestUrlField
                      key={item.urlTarget.field}
                      item={item}
                      app={app}
                      disabled={disabled}
                      onEdit={onEdit}
                    />
                  );

                if (item.kind === "workflow-picker")
                  return (
                    <WorkflowPickerItem
                      key={`workflow-picker:${item.label}`}
                      item={item}
                      app={app}
                      disabled={disabled}
                    />
                  );

                if (item.kind === "exposed-routes")
                  return (
                    <ConnectorRoutesItem
                      key={`exposed-routes:${item.label}`}
                      item={item}
                      app={app}
                    />
                  );

                return (
                  <Fragment
                    key={`${item.target.kind}:${item.target.field}:${item.label}`}
                  >
                    <FieldItem
                      item={item}
                      app={app}
                      locallySet={locallySet}
                      onFieldSet={onFieldSet}
                      disabled={disabled}
                      onEdit={onEdit}
                    />
                    {isNameField && <NoteField app={app} disabled={disabled} />}
                  </Fragment>
                );
              })}
            </Step>
          );
        })}
      </Container>
    </Wrapper>
  );
}
